import { Sandbox } from "@superego/app-sandbox/host";
import type {
  AppComponentProps,
  IntlMessages,
  Settings,
} from "@superego/app-sandbox/types";
import type {
  App,
  CollectionId,
  Document,
  DocumentId,
  DocumentVersionId,
} from "@superego/backend";
import { DocumentContentChangeType } from "@superego/backend";
import { fromHref, RouteName, toHref } from "@superego/routing";
import { makeSuccessfulResult } from "@superego/shared-utils";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import DataLoader from "../../../business-logic/backend/DataLoader.js";
import { useGlobalData } from "../../../business-logic/backend/GlobalData.js";
import {
  listDocumentsQuery,
  useCreateDocument,
  useCreateNewDocumentVersion,
} from "../../../business-logic/backend/hooks.js";
import useBackend from "../../../business-logic/backend/useBackend.js";
import useNavigationState from "../../../business-logic/navigation/useNavigationState.js";
import useTheme from "../../../business-logic/theme/useTheme.js";
import CollectionUtils from "../../../utils/CollectionUtils.js";
import DeleteDocumentModalForm from "../DeleteDocumentModalForm/DeleteDocumentModalForm.js";
import * as cs from "./AppRenderer.css.js";
import getIntlMessages from "./getIntlMessages.js";
import IncompatibilityWarning from "./IncompatibilityWarning.js";

interface Props {
  app: App;
}
export default function AppRenderer({ app }: Props) {
  const appBackend = useBackend();
  const permissions = app.permissions;
  const intl = useIntl();
  const theme = useTheme();
  const { navigateTo } = useNavigationState();
  const { collections } = useGlobalData();
  const collectionsById = CollectionUtils.makeByIdMap(collections);

  const [deleteModalState, setDeleteModalState] = useState<{
    collectionId: CollectionId;
    documentId: DocumentId;
  } | null>(null);
  const createNewDocumentVersionMutation = useCreateNewDocumentVersion();

  const backend = {
    state: {
      get: () => appBackend.apps.getState(app.id, app.latestVersion.id),
      update: (latestRevision: number, content: any) =>
        appBackend.apps.updateState(
          app.id,
          app.latestVersion.id,
          latestRevision,
          content,
        ),
    },
    // Pass these as mutation so the query cache is automatically invalidated.
    documents: {
      create: useCreateDocument().mutate,
      createNewVersion: (
        collectionId: CollectionId,
        documentId: DocumentId,
        latestVersionId: DocumentVersionId,
        content: any,
      ) =>
        createNewDocumentVersionMutation.mutate(
          collectionId,
          documentId,
          latestVersionId,
          { type: DocumentContentChangeType.Full, content },
        ),
      delete: (collectionId: CollectionId, documentId: DocumentId) => {
        setDeleteModalState({ collectionId, documentId });
        return makeSuccessfulResult(null);
      },
    },
    files: {
      getContent: appBackend.files.getContent,
    },
  };

  const [incompatibilityWarningDismissed, setIncompatibilityWarningDismissed] =
    useState(false);
  const [previousAppId, setPreviousAppId] = useState(app.id);
  if (previousAppId !== app.id) {
    setPreviousAppId(app.id);
    setIncompatibilityWarningDismissed(false);
  }

  const settings: Settings = useMemo(() => ({ theme }), [theme]);
  const intlMessages: IntlMessages = useMemo(
    () => getIntlMessages(intl),
    [intl],
  );

  const sandboxNavigateTo = useCallback(
    (href: string) => {
      navigateTo(fromHref(new URL(href, window.location.origin).pathname));
    },
    [navigateTo],
  );

  const isCompatible = app.latestVersion.targetCollections.every(
    (targetCollection) =>
      collectionsById[targetCollection.id]?.latestVersion.id ===
      targetCollection.versionId,
  );
  if (!isCompatible && !incompatibilityWarningDismissed) {
    return (
      <IncompatibilityWarning
        app={app}
        onDismiss={() => setIncompatibilityWarningDismissed(true)}
      />
    );
  }

  const targetCollections = app.latestVersion.targetCollections
    .map((targetCollection) => collectionsById[targetCollection.id])
    .filter((collection) => collection !== undefined);
  return (
    <>
      <DataLoader
        queries={targetCollections.map(({ id }) =>
          listDocumentsQuery([id, false]),
        )}
      >
        {(...documentsLists) => (
          <Sandbox
            key={`${app.id}:${app.latestVersion.id}:${JSON.stringify(permissions)}`}
            backend={backend}
            permissions={permissions}
            navigateTo={sandboxNavigateTo}
            iframeSrc={
              import.meta.env["VITE_SANDBOX_URL"] ??
              "http://app-sandbox.localhost:5173/app-sandbox.html"
            }
            appName={app.name}
            appCode={app.latestVersion.files["/main.tsx"].compiled}
            appProps={{
              collections: Object.fromEntries(
                targetCollections.map((collection, index) => [
                  collection.id,
                  {
                    id: collection.id,
                    versionId: collection.latestVersion.id,
                    displayName: CollectionUtils.getDisplayName(collection),
                    documents: documentsLists[index]!.map((document) => ({
                      id: document.id,
                      versionId: document.latestVersion.id,
                      href: `${window.location.origin}${toHref({
                        name: RouteName.Document,
                        collectionId: collection.id,
                        documentId: document.id,
                      })}`,
                      content: (document as Document).latestVersion.content,
                    })),
                  },
                ]),
              ) satisfies AppComponentProps["collections"],
            }}
            settings={settings}
            intlMessages={intlMessages}
            className={cs.AppRenderer.sandbox}
          />
        )}
      </DataLoader>
      {deleteModalState !== null ? (
        <DeleteDocumentModalForm
          collectionId={deleteModalState.collectionId}
          documentId={deleteModalState.documentId}
          isOpen={true}
          onClose={() => setDeleteModalState(null)}
        />
      ) : null}
    </>
  );
}
